

import React, { useMemo, useState } from "react";

const CONTRACT_TYPES = ["GCC 2015", "FIDIC 1999 Yellow Book"];

const CLAIM_TYPES = [
  "Extension of Time",
  "Additional Payment / Variation",
  "Disruption / Loss and Expense",
  "Acceleration",
  "Standing Time",
  "Other",
];

const QUESTION_TREE = {
  common: [
    {
      id: "eventDescription",
      category: "Event",
      question: "Briefly describe the event giving rise to the claim.",
      type: "textarea",
      required: true,
    },
    {
      id: "causeOfEvent",
      category: "Cause",
      question: "What caused the event, and who does the contractor allege is responsible?",
      type: "textarea",
      required: true,
    },
    {
      id: "instructionEvidence",
      category: "Evidence",
      question: "Was there a written instruction, drawing revision, RFI response, VO, site instruction, or other record supporting the event?",
      type: "textarea",
      required: true,
    },
    {
      id: "noticeIssued",
      category: "Notice",
      question: "Was a formal notice of claim submitted within the contractual notice period? Include the date and reference number if available.",
      type: "textarea",
      required: true,
    },
    {
      id: "contemporaneousRecords",
      category: "Records",
      question: "What contemporaneous records support the claim? For example: site diaries, photos, programmes, minutes, delivery notes, test results, weather records, or correspondence.",
      type: "textarea",
      required: true,
    },
  ],
  time: [
    {
      id: "programmeBaseline",
      category: "Programme",
      question: "Which accepted programme or baseline is being used to assess the delay?",
      type: "text",
      required: true,
    },
    {
      id: "criticalPathImpact",
      category: "Delay",
      question: "Did the event affect the critical path? Explain which activity was affected and how completion was delayed.",
      type: "textarea",
      required: true,
    },
    {
      id: "delayPeriod",
      category: "Delay",
      question: "What delay period is claimed? State start date, end date, and number of days claimed.",
      type: "textarea",
      required: true,
    },
    {
      id: "concurrentDelay",
      category: "Risk",
      question: "Were there any concurrent contractor-caused delays during the same period?",
      type: "textarea",
      required: false,
    },
  ],
  money: [
    {
      id: "costBreakdown",
      category: "Quantum",
      question: "Provide the claimed amount and a breakdown by labour, plant, materials, subcontractors, preliminaries, mark-up, or other cost headings.",
      type: "textarea",
      required: true,
    },
    {
      id: "rateBasis",
      category: "Quantum",
      question: "What is the basis of valuation? BOQ rate, new rate, daywork, quotation, actual cost, or agreed VO?",
      type: "textarea",
      required: true,
    },
    {
      id: "paymentHistory",
      category: "Payment",
      question: "Has any part of this work already been certified, paid, rejected, or included in another VO/claim?",
      type: "textarea",
      required: false,
    },
  ],
  gcc2015: [
    {
      id: "gccClauseBasis",
      category: "Contract",
      question: "Which GCC 2015 clause or contractual mechanism is the contractor relying on?",
      type: "text",
      required: false,
    },
    {
      id: "engineerInstructionGcc",
      category: "Contract",
      question: "Was the work instructed by the Engineer, or is the contractor alleging an implied instruction/change in scope?",
      type: "textarea",
      required: true,
    },
  ],
  fidic1999: [
    {
      id: "fidicClauseBasis",
      category: "Contract",
      question: "Which FIDIC 1999 clause is relied upon? For example Clause 20.1, 8.4, 13, 12, or another clause.",
      type: "text",
      required: false,
    },
    {
      id: "fidicDetermination",
      category: "Contract",
      question: "Has the Engineer consulted both parties and proceeded toward a fair determination? If not, what step is outstanding?",
      type: "textarea",
      required: false,
    },
  ],
};

const initialClaim = {
  contractType: "GCC 2015",
  claimType: "Extension of Time",
  claimReference: "",
  contractor: "",
  projectName: "",
  submittedDate: "",
  claimedDays: "",
  claimedAmount: "",
  summary: "",
};

function normaliseCurrency(value) {
  const number = Number(String(value || "").replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(number) || number === 0) return "Not stated";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 2,
  }).format(number);
}

function getContractQuestionSet(contractType) {
  if (contractType === "FIDIC 1999 Yellow Book") return QUESTION_TREE.fidic1999;
  return QUESTION_TREE.gcc2015;
}

function getClaimQuestionSet(claimType) {
  if (claimType === "Extension of Time" || claimType === "Acceleration" || claimType === "Standing Time") {
    return QUESTION_TREE.time;
  }

  if (
    claimType === "Additional Payment / Variation" ||
    claimType === "Disruption / Loss and Expense" ||
    claimType === "Other"
  ) {
    return QUESTION_TREE.money;
  }

  return [];
}

function buildReviewFlags(answers, questions) {
  const missingRequired = questions.filter((item) => item.required && !String(answers[item.id] || "").trim());
  const flags = [];

  if (missingRequired.length > 0) {
    flags.push({
      level: "High",
      title: "Required information missing",
      detail: `${missingRequired.length} required answer(s) still need to be completed before a reliable recommendation can be made.`,
    });
  }

  if (!String(answers.noticeIssued || "").trim()) {
    flags.push({
      level: "High",
      title: "Notice position unclear",
      detail: "The claim cannot be assessed properly until the notice date, notice reference, and contractual notice compliance are confirmed.",
    });
  }

  if (!String(answers.contemporaneousRecords || "").trim()) {
    flags.push({
      level: "Medium",
      title: "Supporting records not identified",
      detail: "The assessment should request contemporaneous records before accepting entitlement, delay, or quantum.",
    });
  }

  if (!String(answers.criticalPathImpact || "").trim() && questions.some((item) => item.id === "criticalPathImpact")) {
    flags.push({
      level: "High",
      title: "Critical path impact not proven",
      detail: "An EOT recommendation should not be finalised until the affected activity and completion impact are demonstrated.",
    });
  }

  if (!String(answers.costBreakdown || "").trim() && questions.some((item) => item.id === "costBreakdown")) {
    flags.push({
      level: "High",
      title: "Quantum not substantiated",
      detail: "Payment cannot be recommended without a cost breakdown and valuation basis.",
    });
  }

  return flags;
}

function buildRecommendation(claim, answers, questions) {
  const missingRequired = questions.filter((item) => item.required && !String(answers[item.id] || "").trim());

  if (missingRequired.length > 0) {
    return "Interim view only — insufficient information to issue a holistic ruling.";
  }

  if (claim.claimType === "Extension of Time") {
    return "Provisional recommendation: assess entitlement further and only award EOT to the extent that notice compliance, employer-risk event, critical path delay, and contemporaneous records are proven.";
  }

  if (claim.claimType === "Additional Payment / Variation") {
    return "Provisional recommendation: payment may be considered only where the work was instructed or contractually required, properly measured, not previously paid, and valued using the correct contractual basis.";
  }

  return "Provisional recommendation: further contractual and factual assessment required before acceptance, rejection, or partial approval.";
}

export default function ClaudeSection() {
  const [claim, setClaim] = useState(initialClaim);
  const [answers, setAnswers] = useState({});
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [showRuling, setShowRuling] = useState(false);

  const questions = useMemo(() => {
    return [
      ...QUESTION_TREE.common,
      ...getContractQuestionSet(claim.contractType),
      ...getClaimQuestionSet(claim.claimType),
    ];
  }, [claim.contractType, claim.claimType]);

  const activeQuestion = questions[activeQuestionIndex] || questions[0];
  const completedCount = questions.filter((item) => String(answers[item.id] || "").trim()).length;
  const requiredCount = questions.filter((item) => item.required).length;
  const completedRequiredCount = questions.filter(
    (item) => item.required && String(answers[item.id] || "").trim()
  ).length;
  const completionPercent = questions.length === 0 ? 0 : Math.round((completedCount / questions.length) * 100);
  const reviewFlags = buildReviewFlags(answers, questions);
  const recommendation = buildRecommendation(claim, answers, questions);

  function updateClaim(field, value) {
    setClaim((prev) => ({ ...prev, [field]: value }));
    setShowRuling(false);
  }

  function updateAnswer(field, value) {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    setShowRuling(false);
  }

  function resetReview() {
    setClaim(initialClaim);
    setAnswers({});
    setActiveQuestionIndex(0);
    setShowRuling(false);
  }

  function goNext() {
    setActiveQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
  }

  function goPrevious() {
    setActiveQuestionIndex((prev) => Math.max(prev - 1, 0));
  }

  return (
    <section style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Claude Claims Review</h1>
          <p style={styles.subtitle}>
            Guided claim evaluation workflow for GCC 2015 and FIDIC 1999-style assessments.
          </p>
        </div>
        <button type="button" onClick={resetReview} style={styles.secondaryButton}>
          Reset Review
        </button>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>1. Claim Intake</h2>

          <div style={styles.twoColumnGrid}>
            <label style={styles.label}>
              Contract Type
              <select
                value={claim.contractType}
                onChange={(event) => updateClaim("contractType", event.target.value)}
                style={styles.input}
              >
                {CONTRACT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.label}>
              Claim Type
              <select
                value={claim.claimType}
                onChange={(event) => updateClaim("claimType", event.target.value)}
                style={styles.input}
              >
                {CLAIM_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.label}>
              Claim Reference
              <input
                value={claim.claimReference}
                onChange={(event) => updateClaim("claimReference", event.target.value)}
                placeholder="e.g. CLM-001"
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Contractor
              <input
                value={claim.contractor}
                onChange={(event) => updateClaim("contractor", event.target.value)}
                placeholder="Contractor name"
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Project Name
              <input
                value={claim.projectName}
                onChange={(event) => updateClaim("projectName", event.target.value)}
                placeholder="Project name"
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Date Submitted
              <input
                type="date"
                value={claim.submittedDate}
                onChange={(event) => updateClaim("submittedDate", event.target.value)}
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Days Claimed
              <input
                value={claim.claimedDays}
                onChange={(event) => updateClaim("claimedDays", event.target.value)}
                placeholder="e.g. 14"
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              Amount Claimed
              <input
                value={claim.claimedAmount}
                onChange={(event) => updateClaim("claimedAmount", event.target.value)}
                placeholder="e.g. 250000"
                style={styles.input}
              />
            </label>
          </div>

          <label style={{ ...styles.label, marginTop: 14 }}>
            Contractor's Claim Summary
            <textarea
              value={claim.summary}
              onChange={(event) => updateClaim("summary", event.target.value)}
              placeholder="Paste or summarise the contractor's claim here."
              style={styles.textarea}
            />
          </label>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>2. Interactive Question Tree</h2>

          <div style={styles.progressWrap}>
            <div style={styles.progressHeader}>
              <span>{completionPercent}% complete</span>
              <span>
                Required: {completedRequiredCount}/{requiredCount}
              </span>
            </div>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${completionPercent}%` }} />
            </div>
          </div>

          {activeQuestion && (
            <div style={styles.questionBox}>
              <div style={styles.questionMeta}>
                <span style={styles.badge}>{activeQuestion.category}</span>
                <span>
                  Question {activeQuestionIndex + 1} of {questions.length}
                </span>
              </div>

              <h3 style={styles.questionText}>{activeQuestion.question}</h3>

              {activeQuestion.type === "textarea" ? (
                <textarea
                  value={answers[activeQuestion.id] || ""}
                  onChange={(event) => updateAnswer(activeQuestion.id, event.target.value)}
                  placeholder="Answer here..."
                  style={styles.largeTextarea}
                />
              ) : (
                <input
                  value={answers[activeQuestion.id] || ""}
                  onChange={(event) => updateAnswer(activeQuestion.id, event.target.value)}
                  placeholder="Answer here..."
                  style={styles.input}
                />
              )}

              {activeQuestion.required && <p style={styles.requiredNote}>Required for a reliable recommendation.</p>}

              <div style={styles.buttonRow}>
                <button type="button" onClick={goPrevious} disabled={activeQuestionIndex === 0} style={styles.secondaryButton}>
                  Previous
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={activeQuestionIndex === questions.length - 1}
                  style={styles.primaryButton}
                >
                  Next Question
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.cardTitle}>3. Review Dashboard</h2>
            <p style={styles.subtitle}>This preview is rule-based for now. Later, this is where your Claude API response can appear.</p>
          </div>
          <button type="button" onClick={() => setShowRuling(true)} style={styles.primaryButton}>
            Generate Draft Ruling
          </button>
        </div>

        <div style={styles.summaryGrid}>
          <div style={styles.summaryTile}>
            <strong>Reference</strong>
            <span>{claim.claimReference || "Not stated"}</span>
          </div>
          <div style={styles.summaryTile}>
            <strong>Contract</strong>
            <span>{claim.contractType}</span>
          </div>
          <div style={styles.summaryTile}>
            <strong>Claim Type</strong>
            <span>{claim.claimType}</span>
          </div>
          <div style={styles.summaryTile}>
            <strong>Days Claimed</strong>
            <span>{claim.claimedDays || "Not stated"}</span>
          </div>
          <div style={styles.summaryTile}>
            <strong>Amount Claimed</strong>
            <span>{normaliseCurrency(claim.claimedAmount)}</span>
          </div>
        </div>

        <h3 style={styles.sectionHeading}>Open Issues / Risks</h3>
        {reviewFlags.length === 0 ? (
          <p style={styles.goodText}>No major missing information flags based on the current question tree.</p>
        ) : (
          <div style={styles.flagList}>
            {reviewFlags.map((flag, index) => (
              <div key={`${flag.title}-${index}`} style={styles.flagItem}>
                <span style={flag.level === "High" ? styles.highRisk : styles.mediumRisk}>{flag.level}</span>
                <div>
                  <strong>{flag.title}</strong>
                  <p>{flag.detail}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {showRuling && (
          <div style={styles.rulingBox}>
            <h3 style={styles.sectionHeading}>Draft Holistic Ruling</h3>
            <p>
              <strong>Interim finding:</strong> {recommendation}
            </p>
            <p>
              <strong>Entitlement:</strong> Assess whether the event is an employer-risk event, valid variation,
              instruction, changed condition, or other compensable event under {claim.contractType}.
            </p>
            <p>
              <strong>Time:</strong> Confirm programme basis, critical path impact, delay period, concurrency, and whether
              the claimed days are supported by records.
            </p>
            <p>
              <strong>Quantum:</strong> Confirm that the claimed amount is measured, valued under the correct contractual
              mechanism, not duplicated elsewhere, and supported by records.
            </p>
            <p>
              <strong>Next action:</strong> Request unanswered information, then issue a recommendation as accepted,
              rejected, or partially accepted with reasons.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

const styles = {
  page: {
    padding: 24,
    background: "#f8fafc",
    minHeight: "100vh",
    color: "#0f172a",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 14,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 18,
    marginTop: 18,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.06)",
    marginTop: 18,
  },
  cardTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  label: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    background: "#ffffff",
  },
  textarea: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    minHeight: 90,
    resize: "vertical",
  },
  largeTextarea: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    minHeight: 130,
    resize: "vertical",
    width: "100%",
    boxSizing: "border-box",
  },
  progressWrap: {
    marginTop: 14,
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    color: "#64748b",
    fontSize: 13,
    marginBottom: 8,
  },
  progressTrack: {
    height: 10,
    background: "#e2e8f0",
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#2563eb",
    transition: "width 0.2s ease",
  },
  questionBox: {
    marginTop: 16,
    padding: 14,
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    background: "#f8fafc",
  },
  questionMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    color: "#64748b",
    fontSize: 13,
    marginBottom: 10,
  },
  badge: {
    background: "#dbeafe",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "3px 9px",
    fontWeight: 800,
  },
  questionText: {
    margin: "0 0 12px",
    fontSize: 16,
    lineHeight: 1.4,
  },
  requiredNote: {
    margin: "8px 0 0",
    color: "#b45309",
    fontSize: 13,
    fontWeight: 700,
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 14,
    flexWrap: "wrap",
  },
  primaryButton: {
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    background: "#2563eb",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "10px 14px",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 16,
  },
  summaryTile: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#f8fafc",
  },
  sectionHeading: {
    margin: "18px 0 10px",
    fontSize: 16,
    fontWeight: 800,
  },
  goodText: {
    color: "#15803d",
    fontWeight: 700,
  },
  flagList: {
    display: "grid",
    gap: 10,
  },
  flagItem: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#ffffff",
  },
  highRisk: {
    minWidth: 64,
    textAlign: "center",
    borderRadius: 999,
    padding: "4px 8px",
    background: "#fee2e2",
    color: "#b91c1c",
    fontWeight: 900,
    fontSize: 12,
  },
  mediumRisk: {
    minWidth: 64,
    textAlign: "center",
    borderRadius: 999,
    padding: "4px 8px",
    background: "#fef3c7",
    color: "#92400e",
    fontWeight: 900,
    fontSize: 12,
  },
  rulingBox: {
    marginTop: 14,
    border: "1px solid #bfdbfe",
    borderRadius: 14,
    padding: 14,
    background: "#eff6ff",
    lineHeight: 1.55,
  },
};